import { useMutation } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod/v4";

import { addDangerFlash, addSuccessFlash } from "@/components/flash/flashReducer";
import TTModal from "@/components/TTModal";
import { SaveButton } from "@/containers/buttons";
import RHInput from "@/containers/RHInput";
import { MutateSessionUserPasswordDocument } from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { sessionSelector, setCurrentUser } from "@/store/sessionReducer";

const validationSchema = z
  .object({
    password: z
      .string()
      .trim()
      .nonempty("admin:users.profile.password_missing")
      .min(12, "admin:users.profile.password_to_short"),
    passwordConfirm: z.string().trim().nonempty("admin:users.profile.password_confirmation_missing"),
  })
  .check((ctx) => {
    if (ctx.value.password !== ctx.value.passwordConfirm) {
      ctx.issues.push({
        code: "custom",
        message: "admin:users.profile.passwords_do_not_match",
        input: ctx.value.passwordConfirm,
        path: ["passwordConfirm"],
      });
    }
  });

type ValuesType = z.infer<typeof validationSchema>;

const INITIAL_VALUES: ValuesType = {
  password: "",
  passwordConfirm: "",
};

export default function PasswordResetModal() {
  const dispatch = useAppDispatch();
  const [mutateUserPassword] = useMutation(MutateSessionUserPasswordDocument);
  const { t } = useTranslation(["admin"]);
  const { currentUser } = useAppSelector(sessionSelector);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ValuesType>({
    resolver: zodResolver(validationSchema),
    defaultValues: INITIAL_VALUES,
    mode: "onBlur",
  });

  async function updateUserPassword(values: ValuesType) {
    try {
      const { data } = await mutateUserPassword({
        variables: {
          password: values.password,
        },
      });

      if (!data?.mutateUserPassword) {
        throw new MutationError();
      }

      dispatch(setCurrentUser(data.mutateUserPassword));
      dispatch(addSuccessFlash(t("admin:users.profile.password_changed")));
    } catch (e) {
      dispatch(addDangerFlash(t("translation:global.general_error")));
      handleError(e);
    }
  }

  if (!currentUser.passwordReset) return null;

  return (
    <TTModal show={true} size="lg">
      <Form onSubmit={handleSubmit(updateUserPassword)}>
        <Modal.Header>
          <Modal.Title>{t("admin:users.profile.change_password")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Label htmlFor="password">{t("admin:users.profile.change_your_password")}</Form.Label>

          <RHInput
            control={control}
            name="password"
            id="password"
            type="password"
            label={t("admin:users.field_names.password")}
            namespace="admin"
          />

          <RHInput
            control={control}
            name="passwordConfirm"
            id="passwordConfirm"
            type="password"
            label={t("admin:users.field_names.password_confirm")}
            namespace="admin"
          />
        </Modal.Body>

        <Modal.Footer>
          <SaveButton type="submit" disabled={isSubmitting}>
            {t("translation:global.save")}
          </SaveButton>
        </Modal.Footer>
      </Form>
    </TTModal>
  );
}
